/**
 * GovernancePipelineFlow
 *
 * Horizontal pipeline flow visualization showing governance stages.
 * Used on Control Plane dashboard and governance overview pages.
 */
import React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { governanceTokens } from './design-tokens';

export interface PipelineStep {
  id: string;
  label: string;
  icon?: React.ReactNode;
  status: 'success' | 'warning' | 'blocked' | 'inactive' | 'pending';
  metadata?: string;
}

export interface GovernancePipelineFlowProps {
  steps: PipelineStep[];
  activeStep?: string;
}

const statusColors: Record<PipelineStep['status'], string> = {
  success: governanceTokens.colorStatusSuccess,
  warning: governanceTokens.colorStatusWarning,
  blocked: governanceTokens.colorStatusError,
  // #475569 (slate grey) is part of the governance diagram's dark-surface accent
  // scheme, not the brand palette — no token represents it, left as-is.
  inactive: '#475569',
  pending: governanceTokens.colorGovernanceSecondary,
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    overflowX: 'auto',
    padding: `${tokens.spacingVerticalM} 0`,
    '@media (max-width: 640px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    flex: '1 1 0',
    minWidth: '100px',
    position: 'relative',
  },
  nodeOuter: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1,
    transition: 'box-shadow 0.3s ease',
  },
  nodeIcon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForegroundStaticInverted,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  metadata: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  connector: {
    flex: '1 1 0',
    height: '2px',
    minWidth: '24px',
    alignSelf: 'center',
    marginTop: `calc(${tokens.spacingVerticalXXL} * -1)`,
    '@media (max-width: 640px)': {
      width: '2px',
      height: '16px',
      minWidth: 'unset',
      alignSelf: 'center',
      marginTop: 0,
    },
  },
});

export const GovernancePipelineFlow: React.FC<GovernancePipelineFlowProps> = ({
  steps,
  activeStep,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      {steps.map((step, idx) => {
        const isActive = step.id === activeStep;
        const color = statusColors[step.status];
        const glowStyle = isActive
          ? { boxShadow: `0 0 12px ${governanceTokens.colorPipelineGlow}` }
          : undefined;
        const pulseStyle =
          step.status === 'blocked'
            ? { animation: 'pulse 2s ease-in-out infinite' }
            : undefined;

        return (
          <React.Fragment key={step.id}>
            {idx > 0 && (
              <div
                className={styles.connector}
                style={{
                  backgroundColor:
                    steps[idx - 1].status === 'success'
                      ? governanceTokens.colorPipelineConnector
                      : governanceTokens.colorGovernanceDivider,
                }}
              />
            )}
            <div className={styles.step}>
              <div
                className={styles.nodeOuter}
                style={{
                  backgroundColor: color,
                  ...glowStyle,
                  ...pulseStyle,
                }}
              >
                <span className={styles.nodeIcon}>{step.icon}</span>
              </div>
              <span className={styles.label}>{step.label}</span>
              {step.metadata && (
                <span className={styles.metadata}>{step.metadata}</span>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
